import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface GoogleSheetsStatusConnected {
  connected: true;
  googleEmail: string;
  spreadsheetId: string | null;
  spreadsheetName: string | null;
  spreadsheetUrl: string | null;
  syncQuotes: boolean;
  syncInvoices: boolean;
  syncPayments: boolean;
  setupCompleted: boolean;
  backfillStatus: "queued" | "running" | "done" | "failed" | string | null;
  lastSyncAt: string | null;
  lastSyncStatus: "ok" | "failed" | "errored" | string | null;
  lastSyncError: string | null;
}

export interface GoogleSheetsStatusDisconnected {
  connected: false;
}

export type GoogleSheetsStatus =
  | GoogleSheetsStatusConnected
  | GoogleSheetsStatusDisconnected;

export interface GoogleDriveSpreadsheetFile {
  id: string;
  name: string;
  webViewLink?: string | null;
  modifiedTime?: string | null;
}

export interface GoogleSheetsSetupBody {
  mode: "create" | "existing";
  spreadsheetId?: string;
  title?: string;
  syncQuotes: boolean;
  syncInvoices: boolean;
  syncPayments: boolean;
}

export interface GoogleSheetsSetupResult {
  ok: true;
  spreadsheetId: string;
  spreadsheetName: string;
  spreadsheetUrl: string;
}

export const GOOGLE_SHEETS_STATUS_QUERY_KEY = [
  "/api/integrations/google-sheets/status",
] as const;

export const GOOGLE_SHEETS_SPREADSHEETS_QUERY_KEY = [
  "/api/integrations/google-sheets/spreadsheets",
] as const;

export const getGoogleSheetsStatus = async (): Promise<GoogleSheetsStatus> =>
  customFetch<GoogleSheetsStatus>("/api/integrations/google-sheets/status");

export const useGetGoogleSheetsStatus = <
  TData = GoogleSheetsStatus,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<GoogleSheetsStatus, TError, TData>;
}) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? GOOGLE_SHEETS_STATUS_QUERY_KEY;
  return useQuery<GoogleSheetsStatus, TError, TData>({
    queryKey,
    queryFn: getGoogleSheetsStatus,
    ...queryOptions,
  });
};

export const listGoogleSpreadsheets = async (): Promise<{
  files: GoogleDriveSpreadsheetFile[];
}> =>
  customFetch<{ files: GoogleDriveSpreadsheetFile[] }>(
    "/api/integrations/google-sheets/spreadsheets",
  );

export const useListGoogleSpreadsheets = <
  TData = { files: GoogleDriveSpreadsheetFile[] },
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<{ files: GoogleDriveSpreadsheetFile[] }, TError, TData>;
}) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? GOOGLE_SHEETS_SPREADSHEETS_QUERY_KEY;
  return useQuery<{ files: GoogleDriveSpreadsheetFile[] }, TError, TData>({
    queryKey,
    queryFn: listGoogleSpreadsheets,
    ...queryOptions,
  });
};

export const setupGoogleSheetsSync = async (
  body: GoogleSheetsSetupBody,
): Promise<GoogleSheetsSetupResult> =>
  customFetch<GoogleSheetsSetupResult>(
    "/api/integrations/google-sheets/setup",
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "content-type": "application/json" },
    },
  );

export const useSetupGoogleSheetsSync = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    GoogleSheetsSetupResult,
    TError,
    GoogleSheetsSetupBody,
    TContext
  >;
}) => {
  return useMutation<GoogleSheetsSetupResult, TError, GoogleSheetsSetupBody, TContext>({
    mutationFn: setupGoogleSheetsSync,
    ...options?.mutation,
  });
};

export const syncGoogleSheetsNow = async (): Promise<{ ok: true }> =>
  customFetch<{ ok: true }>("/api/integrations/google-sheets/sync-now", {
    method: "POST",
  });

export const useSyncGoogleSheetsNow = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<{ ok: true }, TError, void, TContext>;
}) => {
  return useMutation<{ ok: true }, TError, void, TContext>({
    mutationFn: syncGoogleSheetsNow,
    ...options?.mutation,
  });
};

export const disconnectGoogleSheets = async (): Promise<{ ok: true }> =>
  customFetch<{ ok: true }>("/api/integrations/google-sheets/disconnect", {
    method: "POST",
  });

export const useDisconnectGoogleSheets = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<{ ok: true }, TError, void, TContext>;
}) => {
  return useMutation<{ ok: true }, TError, void, TContext>({
    mutationFn: disconnectGoogleSheets,
    ...options?.mutation,
  });
};
